import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { NumberDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionConfiguration } from "../../../../../rule-engine-core/Expressions/ExpressionConfiguration";
import { GreaterThanExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThan";
import { StringEqualExpression } from "../../../../../rule-engine-core/Expressions/Expressions/String/StringEqualExpression";
import { RuleTableConfiguration } from "../../../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";
import { RuleTableColumn } from "../../../../../rule-engine-core/Rule/Table/Rule/Structure/RuleTableRuleColumn";
import { RuleTableRow } from "../../../../../rule-engine-core/Rule/Table/Rule/Structure/RuleTableRow";
import { generateManufacturers, generatePriceToleranceRules } from "../../../data/generateManufacturers";
import type { PriceToleranceRule, SupplierPriceUpdate } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import type { AppGlueStorageBackedConfig } from "../../ruleIntegrationTypes";
import { createRuleSummaryPresentation } from "../../ruleIntegrationTypes";
import type { CreateRuleTableIntegrationAdapterParams } from "../../executableFamilies/ruleTable/RuleTableIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import {
    bindRuleTableCellExpression,
    normalizeRuleTableCellExpressions,
} from "./ruleTableExpressionBinding";

export const TR008_RULE_ID = "TR-008";
export const TR008_APPGLUE_EXECUTABLE_TYPE = "RuleTableConfiguration";

export type Tr008PriceToleranceOutcome = "auto_approved" | "flagged" | "held";

export type Tr008RuleTableResult = {
    outcome: Tr008PriceToleranceOutcome;
    status: DemoRuleStatus;
    output_queue: DemoOutputQueue;
    reason_code: string;
    explanation: string;
    manufacturer_id: string;
    manufacturer_name: string;
    auto_approve_threshold_pct: number;
    review_threshold_pct: number;
    action_on_breach: string;
};

export type Tr008RuleTableRowSummary = Partial<Tr008RuleTableResult> & {
    rowId: string;
};

const RESULT_COLUMN_NAMES = [
    "outcome",
    "status",
    "output_queue",
    "reason_code",
    "explanation",
    "manufacturer_id",
    "manufacturer_name",
    "auto_approve_threshold_pct",
    "review_threshold_pct",
    "action_on_breach",
] as const;

const OUTPUT_QUEUES = ["epicor_cost_file_updates", "procurement_review_queue"] as const;

const createInputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("manufacturer_id"));
    inputData.fields.push(new NumberDataDefinition("price_change_pct"));
    return inputData;
};

const createResultDefinition = (): DataDefinition => {
    const resultData = new DataDefinition();
    resultData.fields.push(new StringDataDefinition("outcome"));
    resultData.fields.push(new StringDataDefinition("status"));
    resultData.fields.push(new StringDataDefinition("output_queue"));
    resultData.fields.push(new StringDataDefinition("reason_code"));
    resultData.fields.push(new StringDataDefinition("explanation"));
    resultData.fields.push(new StringDataDefinition("manufacturer_id"));
    resultData.fields.push(new StringDataDefinition("manufacturer_name"));
    resultData.fields.push(new NumberDataDefinition("auto_approve_threshold_pct"));
    resultData.fields.push(new NumberDataDefinition("review_threshold_pct"));
    resultData.fields.push(new StringDataDefinition("action_on_breach"));
    return resultData;
};

export const buildTr008ExecutionData = (update: SupplierPriceUpdate): ExecutionData =>
    new ExecutionData({
        manufacturer_id: update.manufacturerId,
        price_change_pct: update.priceChangePct,
    });

const expressionConfig = (expression: StringEqualExpression | GreaterThanExpression): ExpressionConfiguration => {
    const config = new ExpressionConfiguration();
    config.expression = expression;
    return config;
};

const trueExpressionConfig = (): ExpressionConfiguration => {
    const config = new ExpressionConfiguration();
    config.allowTrueValueResult = true;
    config.value = true;
    return config;
};

const manufacturerEquals = (manufacturerId: string): StringEqualExpression => {
    const expression = new StringEqualExpression();
    expression.value1.variableName = "manufacturer_id";
    expression.value2.value = manufacturerId;
    return expression;
};

const priceChangeGreaterThan = (thresholdPct: number): GreaterThanExpression => {
    const expression = new GreaterThanExpression();
    expression.value1.variableName = "price_change_pct";
    expression.value2.value = thresholdPct;
    return expression;
};

const getResultColumnId = (config: RuleTableConfiguration, name: (typeof RESULT_COLUMN_NAMES)[number]): string => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) throw new Error(`TR-008 rule table is missing result column ${name}.`);
    return column._id;
};

const setResultCellValue = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    name: (typeof RESULT_COLUMN_NAMES)[number],
    value: string | number,
) => {
    row.resultCellsData[getResultColumnId(config, name)].value = value;
};

const getResultCellValue = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    name: (typeof RESULT_COLUMN_NAMES)[number],
): unknown => {
    const column = config.resultColumns.find((candidate) => candidate.name === name);
    if (!column) return undefined;
    return row.resultCellsData[column._id]?.value;
};

const statusForOutcome = (outcome: Tr008PriceToleranceOutcome): DemoRuleStatus => {
    if (outcome === "held") return "rejected";
    if (outcome === "flagged") return "flagged";
    return "pass";
};

const outputQueueForOutcome = (outcome: Tr008PriceToleranceOutcome): DemoOutputQueue =>
    outcome === "auto_approved" ? "epicor_cost_file_updates" : "procurement_review_queue";

const reasonForOutcome = (outcome: Tr008PriceToleranceOutcome): string => {
    if (outcome === "held") return "PRICE_HELD";
    if (outcome === "flagged") return "PRICE_REVIEW_REQUIRED";
    return "PRICE_AUTO_APPROVED";
};

const explanationForOutcome = (outcome: Tr008PriceToleranceOutcome): string => {
    if (outcome === "held") {
        return "Price change exceeds the manufacturer review threshold, so cost-file sync is held.";
    }
    if (outcome === "flagged") {
        return "Price change is above auto-approve tolerance but within review tolerance.";
    }
    return "Price change is within manufacturer auto-approve tolerance.";
};

const resultForOutcome = (rule: PriceToleranceRule, outcome: Tr008PriceToleranceOutcome): Tr008RuleTableResult => ({
    outcome,
    status: statusForOutcome(outcome),
    output_queue: outputQueueForOutcome(outcome),
    reason_code: reasonForOutcome(outcome),
    explanation: explanationForOutcome(outcome),
    manufacturer_id: rule.manufacturerId,
    manufacturer_name: rule.manufacturerName,
    auto_approve_threshold_pct: rule.autoApproveThresholdPct,
    review_threshold_pct: rule.reviewThresholdPct,
    action_on_breach: rule.actionOnBreach,
});

const setRowResult = (config: RuleTableConfiguration, row: RuleTableRow, result: Tr008RuleTableResult) => {
    RESULT_COLUMN_NAMES.forEach((name) => setResultCellValue(config, row, name, result[name]));
};

const setRowRuleExpressions = (
    config: RuleTableConfiguration,
    row: RuleTableRow,
    manufacturerColumn: RuleTableColumn,
    priceChangeColumn: RuleTableColumn,
    manufacturerId: string,
    priceExpression: ExpressionConfiguration,
) => {
    bindRuleTableCellExpression(
        config,
        row,
        manufacturerColumn,
        expressionConfig(manufacturerEquals(manufacturerId)),
    );
    bindRuleTableCellExpression(config, row, priceChangeColumn, priceExpression);
};

const addToleranceRow = (
    config: RuleTableConfiguration,
    columns: { manufacturer: RuleTableColumn; priceChange: RuleTableColumn },
    rule: PriceToleranceRule,
    outcome: Tr008PriceToleranceOutcome,
) => {
    const row = config.addRow();
    const priceExpression =
        outcome === "held"
            ? expressionConfig(priceChangeGreaterThan(rule.reviewThresholdPct))
            : outcome === "flagged"
              ? expressionConfig(priceChangeGreaterThan(rule.autoApproveThresholdPct))
              : trueExpressionConfig();

    setRowRuleExpressions(config, row, columns.manufacturer, columns.priceChange, rule.manufacturerId, priceExpression);
    setRowResult(config, row, resultForOutcome(rule, outcome));
};

const ruleIsAlwaysHold = (rule: PriceToleranceRule): boolean =>
    rule.autoApproveThresholdPct <= 0 && rule.reviewThresholdPct <= 0 && /always hold/i.test(rule.actionOnBreach);

const setDefaultResult = (config: RuleTableConfiguration) => {
    const defaultRule: PriceToleranceRule = {
        manufacturerId: "MFR-999",
        manufacturerName: "Unknown / New",
        autoApproveThresholdPct: 0,
        reviewThresholdPct: 0,
        actionOnBreach: "ALWAYS hold",
        notes: "Safe default for new or unmapped manufacturers",
        owner: "Procurement",
        effectiveDate: "2026-05-01",
    };
    const defaultResult = resultForOutcome(defaultRule, "held");

    RESULT_COLUMN_NAMES.forEach((name) => {
        config.defaultResult[getResultColumnId(config, name)].value = defaultResult[name];
    });
};

export const createDefaultTr008PriceToleranceRules = (): PriceToleranceRule[] =>
    generatePriceToleranceRules(generateManufacturers());

export const createTr008PriceToleranceRuleTableConfiguration = (
    rules: PriceToleranceRule[] = createDefaultTr008PriceToleranceRules(),
): RuleTableConfiguration => {
    AppGlueCoreInit.init();

    const config = new RuleTableConfiguration();
    config.title = "TR-008 supplier price tolerance table";
    config.inputData = createInputDefinition();
    config.executionResult = createResultDefinition();

    const manufacturerColumn = config.ruleColumns[0]!;
    config.setRuleColumn(manufacturerColumn, "manufacturer_id");
    const priceChangeColumn = config.addRuleColumn("price_change_pct");
    config.setRuleColumn(priceChangeColumn, "price_change_pct");

    while (config.rows.length > 0) {
        config.removeRow(config.rows[0]!);
    }

    rules.forEach((rule) => {
        if (ruleIsAlwaysHold(rule)) {
            const row = config.addRow();
            setRowRuleExpressions(
                config,
                row,
                manufacturerColumn,
                priceChangeColumn,
                rule.manufacturerId,
                trueExpressionConfig(),
            );
            setRowResult(config, row, resultForOutcome(rule, "held"));
            return;
        }

        addToleranceRow(config, { manufacturer: manufacturerColumn, priceChange: priceChangeColumn }, rule, "held");
        addToleranceRow(config, { manufacturer: manufacturerColumn, priceChange: priceChangeColumn }, rule, "flagged");
        addToleranceRow(
            config,
            { manufacturer: manufacturerColumn, priceChange: priceChangeColumn },
            rule,
            "auto_approved",
        );
    });

    setDefaultResult(config);
    normalizeRuleTableCellExpressions(config);
    return config;
};

const isOutcome = (value: unknown): value is Tr008PriceToleranceOutcome =>
    value === "auto_approved" || value === "flagged" || value === "held";

const isStatus = (value: unknown): value is DemoRuleStatus =>
    value === "pass" || value === "flagged" || value === "rejected" || value === "skipped";

const isOutputQueue = (value: unknown): value is DemoOutputQueue =>
    typeof value === "string" && OUTPUT_QUEUES.includes(value as (typeof OUTPUT_QUEUES)[number]);

const asNumber = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;

const asString = (value: unknown, fallback: string): string => (typeof value === "string" ? value : fallback);

const normalizeRuleTableResult = (result: unknown): Tr008RuleTableResult => {
    const data = result && typeof result === "object" ? (result as Record<string, unknown>) : {};
    const outcome = isOutcome(data.outcome) ? data.outcome : "held";
    const status = isStatus(data.status) ? data.status : statusForOutcome(outcome);
    const outputQueue = isOutputQueue(data.output_queue) ? data.output_queue : outputQueueForOutcome(outcome);

    return {
        outcome,
        status,
        output_queue: outputQueue,
        reason_code: asString(data.reason_code, reasonForOutcome(outcome)),
        explanation: asString(data.explanation, explanationForOutcome(outcome)),
        manufacturer_id: asString(data.manufacturer_id, "MFR-999"),
        manufacturer_name: asString(data.manufacturer_name, "Unknown / New"),
        auto_approve_threshold_pct: asNumber(data.auto_approve_threshold_pct, 0),
        review_threshold_pct: asNumber(data.review_threshold_pct, 0),
        action_on_breach: asString(data.action_on_breach, "ALWAYS hold"),
    };
};

export const evaluateTr008PriceToleranceResult = (
    config: RuleTableConfiguration,
    update: SupplierPriceUpdate,
): Tr008RuleTableResult => normalizeRuleTableResult(config.run(buildTr008ExecutionData(update), undefined));

export const evaluateTr008PriceToleranceOutcome = (
    config: RuleTableConfiguration,
    update: SupplierPriceUpdate,
): Tr008PriceToleranceOutcome => evaluateTr008PriceToleranceResult(config, update).outcome;

const getManufacturerIdFromRow = (row: RuleTableRow): string | undefined =>
    Object.values(row.ruleCells)
        .map((cell) => cell.expression?.expression)
        .map((expression) => {
            if (!(expression instanceof StringEqualExpression)) return undefined;
            return expression.value1.variableName === "manufacturer_id" && typeof expression.value2.value === "string"
                ? expression.value2.value
                : undefined;
        })
        .find(Boolean);

export const getTr008RuleTableRows = (config: RuleTableConfiguration): Tr008RuleTableRowSummary[] =>
    config.rows.map((row) => ({
        rowId: row._id,
        manufacturer_id: getManufacturerIdFromRow(row),
        outcome: isOutcome(getResultCellValue(config, row, "outcome"))
            ? (getResultCellValue(config, row, "outcome") as Tr008PriceToleranceOutcome)
            : undefined,
        status: isStatus(getResultCellValue(config, row, "status"))
            ? (getResultCellValue(config, row, "status") as DemoRuleStatus)
            : undefined,
        output_queue: isOutputQueue(getResultCellValue(config, row, "output_queue"))
            ? (getResultCellValue(config, row, "output_queue") as DemoOutputQueue)
            : undefined,
        reason_code: asString(getResultCellValue(config, row, "reason_code"), ""),
        explanation: asString(getResultCellValue(config, row, "explanation"), ""),
        manufacturer_name: asString(getResultCellValue(config, row, "manufacturer_name"), ""),
        auto_approve_threshold_pct: asNumber(getResultCellValue(config, row, "auto_approve_threshold_pct"), 0),
        review_threshold_pct: asNumber(getResultCellValue(config, row, "review_threshold_pct"), 0),
        action_on_breach: asString(getResultCellValue(config, row, "action_on_breach"), ""),
    }));

const OUTCOME_PRESENTATION_ORDER: Tr008PriceToleranceOutcome[] = ["auto_approved", "flagged", "held"];

const renderTr008ActionLine = (config: RuleTableConfiguration): string => {
    const routingByOutcome = new Map<Tr008PriceToleranceOutcome, string>();

    getTr008RuleTableRows(config).forEach((row) => {
        if (!row.outcome || routingByOutcome.has(row.outcome)) return;

        routingByOutcome.set(
            row.outcome,
            `${row.outcome} -> ${row.output_queue ?? outputQueueForOutcome(row.outcome)}`,
        );
    });

    const routes = OUTCOME_PRESENTATION_ORDER.flatMap((outcome) => {
        const route = routingByOutcome.get(outcome);
        return route ? [route] : [];
    });

    return `ACTION ${routes.length ? routes.join("; ") : "rule-table result routing"}`;
};

export const getTr008PriceToleranceRulesFromRuleTable = (config: RuleTableConfiguration): PriceToleranceRule[] => {
    const rulesByManufacturer = new Map<string, PriceToleranceRule>();

    getTr008RuleTableRows(config).forEach((row) => {
        if (!row.manufacturer_id || rulesByManufacturer.has(row.manufacturer_id)) return;

        rulesByManufacturer.set(row.manufacturer_id, {
            manufacturerId: row.manufacturer_id,
            manufacturerName: row.manufacturer_name ?? row.manufacturer_id,
            autoApproveThresholdPct: row.auto_approve_threshold_pct ?? 0,
            reviewThresholdPct: row.review_threshold_pct ?? 0,
            actionOnBreach: row.action_on_breach ?? "HOLD",
            notes: "Stored in AppGlue RuleTableConfiguration",
            owner: "Product operations",
            effectiveDate: "2026-05-01",
        });
    });

    return Array.from(rulesByManufacturer.values());
};

export const getTr008PriceToleranceRulesFromConfig = (
    config: AppGlueStorageBackedConfig | null,
): PriceToleranceRule[] | null =>
    config instanceof RuleTableConfiguration ? getTr008PriceToleranceRulesFromRuleTable(config) : null;

export const updateTr008PriceToleranceRuleTableRule = (
    config: RuleTableConfiguration,
    manufacturerId: string,
    patch: Partial<Pick<PriceToleranceRule, "autoApproveThresholdPct" | "reviewThresholdPct" | "actionOnBreach">>,
): RuleTableConfiguration => {
    const nextRules = getTr008PriceToleranceRulesFromRuleTable(config).map((rule) =>
        rule.manufacturerId === manufacturerId
            ? {
                  ...rule,
                  autoApproveThresholdPct:
                      patch.autoApproveThresholdPct !== undefined
                          ? patch.autoApproveThresholdPct
                          : rule.autoApproveThresholdPct,
                  reviewThresholdPct:
                      patch.reviewThresholdPct !== undefined ? patch.reviewThresholdPct : rule.reviewThresholdPct,
                  actionOnBreach: patch.actionOnBreach ?? rule.actionOnBreach,
              }
            : rule,
    );

    return createTr008PriceToleranceRuleTableConfiguration(nextRules);
};

const renderTr008Summary = (config: RuleTableConfiguration) => {
    const thresholdRows = getTr008PriceToleranceRulesFromRuleTable(config);
    const sampleThresholds = thresholdRows
        .slice(0, 3)
        .map(
            (row) =>
                `${row.manufacturerName}: ${row.autoApproveThresholdPct}% auto / ${row.reviewThresholdPct}% review`,
        )
        .join(", ");

    return createRuleSummaryPresentation({
        status: "complete",
        lines: [
            `LOOKUP ${thresholdRows.length} manufacturer price tolerance rows`,
            sampleThresholds ? `THRESHOLDS ${sampleThresholds}` : "THRESHOLDS stored in AppGlue rule table",
            renderTr008ActionLine(config),
        ],
        sources: ["native_appglue_config", "adapter_projection"],
    });
};

const sampleUpdate = (overrides: Partial<SupplierPriceUpdate> = {}): SupplierPriceUpdate => ({
    updateId: "PRICE-TR008-SAMPLE",
    partId: "PART-TR008-SAMPLE",
    mpn: "TI-0001SEM",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    supplierName: "Arrow",
    breakQuantity: 100,
    newCost: 1.12,
    currency: "USD",
    updateDate: "2026-05-01",
    sourceFeed: "daily_supplier_feed",
    confidenceScore: 94,
    lastBookedCost: 1,
    ninetyDayAverageCost: 1,
    priceChangePct: 4,
    ...overrides,
});

export const TR008_RULE_TABLE_POLICY: CreateRuleTableIntegrationAdapterParams<SupplierPriceUpdate> = {
    ruleId: TR008_RULE_ID,
    policyBinding: requireRulePolicyBinding(TR008_RULE_ID),
    createConfig: (context = {}) =>
        createTr008PriceToleranceRuleTableConfiguration(
            context.priceToleranceRules ?? context.data?.priceToleranceRules ?? createDefaultTr008PriceToleranceRules(),
        ),
    evaluator: {
        evaluate: (config, update) => {
            const result = evaluateTr008PriceToleranceResult(config, update);

            return {
                passed: result.status === "pass",
                status: result.status,
                reasonCode: result.reason_code,
                explanation: result.explanation,
                outputQueue: result.output_queue,
                comparisons: [
                    {
                        label: "Price change",
                        expected: `<= ${result.review_threshold_pct.toFixed(2)}% review threshold`,
                        actual: `${update.priceChangePct.toFixed(2)}%`,
                        ok: result.outcome !== "held",
                    },
                    {
                        label: "Auto approve",
                        expected: `<= ${result.auto_approve_threshold_pct.toFixed(2)}%`,
                        actual: `${update.priceChangePct.toFixed(2)}%`,
                        ok: result.outcome === "auto_approved",
                    },
                ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: TR008_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence: {
                    ...result,
                    updateId: update.updateId,
                    priceChangePct: update.priceChangePct,
                },
            };
        },
    },
    summaryPresenter: {
        renderSummary: renderTr008Summary,
    },
    demoDataProjection: {
        projectDemoData: (config) => ({
            priceToleranceRules: getTr008PriceToleranceRulesFromRuleTable(config),
        }),
    },
    suggestionCommands: {
        supportsCommand: (command, context) =>
            command.type === "updateRuleTableRows" &&
            command.ruleId === TR008_RULE_ID &&
            command.executableType === TR008_APPGLUE_EXECUTABLE_TYPE &&
            command.rowSelector.column === "manufacturer_id" &&
            context.ruleId === TR008_RULE_ID &&
            context.executableType === TR008_APPGLUE_EXECUTABLE_TYPE &&
            context.config instanceof RuleTableConfiguration,
        applyCommand: (command, config) => {
            if (
                command.type !== "updateRuleTableRows" ||
                command.ruleId !== TR008_RULE_ID ||
                command.executableType !== TR008_APPGLUE_EXECUTABLE_TYPE ||
                command.rowSelector.column !== "manufacturer_id"
            ) {
                return null;
            }

            const patch = {
                autoApproveThresholdPct:
                    typeof command.patch.auto_approve_threshold_pct === "number"
                        ? command.patch.auto_approve_threshold_pct
                        : undefined,
                reviewThresholdPct:
                    typeof command.patch.review_threshold_pct === "number"
                        ? command.patch.review_threshold_pct
                        : undefined,
                actionOnBreach:
                    typeof command.patch.action_on_breach === "string" ? command.patch.action_on_breach : undefined,
            };

            return updateTr008PriceToleranceRuleTableRule(config, String(command.rowSelector.value), patch);
        },
    },
};

export const TR008_RUNTIME_SAMPLES = {
    passingRow: sampleUpdate({ priceChangePct: 4 }),
    routedRow: sampleUpdate({ priceChangePct: 12 }),
};
